"use client";

import React, { useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { Drawer } from "../common/Drawer";
import {
  setCartDrawerOpen,
  removeItem,
  updateQty,
  updateCartQtyAction,
} from "@/redux/slices/cartSlice";
import {
  Trash2,
  ShoppingBag,
  ArrowRight,
  Plus,
  Minus,
  Tag,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Link from "next/link";
import { calculateWholesaleDiscount } from "@/utils/discountUtils";
import {
  getPriceSlabs,
  getProductUnitPrice,
  getCartItemEffectivePricing,
} from "@/utils/priceUtils";
import toast from "react-hot-toast";

export const CartDrawer = () => {
  const dispatch = useAppDispatch();
  const { items, isCartDrawerOpen } = useAppSelector((state) => state.cart);
  const { isCustomerAuthenticated, user } = useAppSelector(
    (state) => state.auth,
  );
  const { ActiveDiscountList } = useAppSelector((state) => state.discount);
  const { isInternational } = useAppSelector((state) => state.location);

  const [expandedTiers, setExpandedTiers] = useState<Record<number, boolean>>({});
  const [localItems, setLocalItems] = useState(() =>
    items.map((it) => {
      const { basePrice, productPrices } = getCartItemEffectivePricing(
        it,
        isInternational,
      );
      const unitPrice = getProductUnitPrice(basePrice, productPrices, it.Qty);
      return {
        ...it,
        BasePrice: basePrice,
        ProductPrices: productPrices,
        Price: unitPrice,
        TotalPrice: it.Qty * unitPrice,
      };
    }),
  );
  const isInitialMount = useRef(true);

  useEffect(() => {
    setLocalItems(
      items.map((it) => {
        const { basePrice, productPrices } = getCartItemEffectivePricing(
          it,
          isInternational,
        );
        const unitPrice = getProductUnitPrice(basePrice, productPrices, it.Qty);
        return {
          ...it,
          BasePrice: basePrice,
          ProductPrices: productPrices,
          Price: unitPrice,
          TotalPrice: it.Qty * unitPrice,
        };
      }),
    );
  }, [items, isInternational]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      localItems.forEach((localItem) => {
        const reduxMatch = items.find(
          (ri) => ri.ProductID === localItem.ProductID,
        );
        if (reduxMatch && reduxMatch.Qty !== localItem.Qty) {
          if (isCustomerAuthenticated && user?.id && localItem.CartID) {
            dispatch(
              updateCartQtyAction({
                CartID: localItem.CartID,
                CustomerID: Number(user.id),
                ProductID: localItem.ProductID,
                Qty: localItem.Qty,
                Price: localItem.Price,
                GSTPer: localItem.GSTPer,
              }),
            );
          } else {
            dispatch(
              updateQty({
                ProductID: localItem.ProductID,
                Qty: localItem.Qty,
              }),
            );
          }
        }
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [localItems, isCustomerAuthenticated, user?.id, dispatch, items]);

  const toggleExpandTier = (productId: number) => {
    setExpandedTiers((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const isFirstOrderEligible =
    !isCustomerAuthenticated || (isCustomerAuthenticated && user?.isFirstOrder);

  const subtotal = localItems.reduce(
    (acc, item) => acc + (item.TotalPrice || 0),
    0,
  );

  const discountableSubtotal = localItems.reduce(
    (acc, item) =>
      item.IsCostToCost || (item as any).isCostToCost
        ? acc
        : acc + (item.TotalPrice || 0),
    0,
  );

  const showLoginDiscount = isFirstOrderEligible && discountableSubtotal >= 3000;
  const loginDiscountAmount = showLoginDiscount ? discountableSubtotal * 0.05 : 0;

  const { percentage: wholesaleDiscountPercent, value: wholesaleDiscountAmount } =
    calculateWholesaleDiscount(discountableSubtotal, ActiveDiscountList);

  const totalDiscountAmount = loginDiscountAmount + wholesaleDiscountAmount;
  const effectiveDiscountMultiplier =
    discountableSubtotal > 0
      ? (discountableSubtotal - totalDiscountAmount) / discountableSubtotal
      : 1;

  const finalPayable = subtotal - totalDiscountAmount;

  const handleClose = () => {
    dispatch(setCartDrawerOpen(false));
  };

  const handleUpdateQty = (item: any, delta: number) => {
    setLocalItems((prev) =>
      prev.map((i) => {
        if (i.ProductID === item.ProductID) {
          const minQty = i.MinimumQty || 1;
          const maxQty =
            i.AvailableQty !== undefined && i.AvailableQty !== null
              ? i.AvailableQty
              : Infinity;

          if (delta > 0 && i.Qty >= maxQty) {
            toast.error(`Only ${maxQty} units available in stock.`);
            return i;
          }

          let newQty = i.Qty + delta;
          if (newQty > maxQty) {
            newQty = maxQty;
            toast.error(
              `Quantity adjusted to maximum available stock (${maxQty}).`,
            );
          }
          newQty = Math.max(minQty, newQty);

          const { basePrice, productPrices } = getCartItemEffectivePricing(
            i,
            isInternational,
          );
          const unitPrice = getProductUnitPrice(
            basePrice,
            productPrices,
            newQty,
          );
          return {
            ...i,
            Qty: newQty,
            BasePrice: basePrice,
            ProductPrices: productPrices,
            Price: unitPrice,
            TotalPrice: newQty * unitPrice,
          };
        }
        return i;
      }),
    );
  };

  const handleSetQty = (item: any, targetQty: number) => {
    const maxQty =
      item.AvailableQty !== undefined && item.AvailableQty !== null
        ? item.AvailableQty
        : Infinity;

    if (targetQty > maxQty) {
      toast.error(
        `Tier requires ${targetQty} pcs, but only ${maxQty} are available in stock. Setting to maximum available.`,
      );
      targetQty = maxQty;
    }

    setLocalItems((prev) =>
      prev.map((i) => {
        if (i.ProductID === item.ProductID) {
          const minQty = i.MinimumQty || 1;
          const newQty = Math.max(minQty, targetQty);
          const { basePrice, productPrices } = getCartItemEffectivePricing(
            i,
            isInternational,
          );
          const unitPrice = getProductUnitPrice(
            basePrice,
            productPrices,
            newQty,
          );
          return {
            ...i,
            Qty: newQty,
            BasePrice: basePrice,
            ProductPrices: productPrices,
            Price: unitPrice,
            TotalPrice: newQty * unitPrice,
          };
        }
        return i;
      }),
    );
  };

  const handleRemove = (productId: number) => {
    if (confirm("Remove this item from your cart?")) {
      const item = localItems.find((i) => i.ProductID === productId);

      if (isCustomerAuthenticated && user?.id && item?.CartID) {
        dispatch(
          updateCartQtyAction({
            CartID: item.CartID,
            CustomerID: Number(user.id),
            ProductID: productId,
            Qty: 0,
            Price: item?.Price || 0,
          }),
        );
      } else {
        dispatch(removeItem(productId));
      }
    }
  };

  return (
    <Drawer
      isOpen={isCartDrawerOpen}
      onClose={handleClose}
      isEscKey={true}
      isOverlayClick={true}
      title={
        <div className="flex items-center gap-3">
          <ShoppingBag size={20} className="text-gold-dim" />
          <span className="font-cormorant text-xl font-semibold tracking-wide">
            Shopping Cart
          </span>
        </div>
      }
      width="sm:w-96"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
          {localItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
              <div className="w-16 h-16 bg-black/5 rounded-full flex items-center justify-center text-text-3">
                <ShoppingBag size={32} />
              </div>
              <div>
                <p className="text-text-1 font-medium">Your cart is empty</p>
                <p className="text-text-3 text-xs mt-1">
                  Add some beautiful jewellery to get started
                </p>
              </div>
              <Link
                href="/shop"
                onClick={handleClose}
                className="btn-gold !py-3 !px-8 text-xs font-bold uppercase tracking-widest mt-4"
              >
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {localItems.map((item) => {
                const isCostToCostProduct =
                  item.IsCostToCost || (item as any).isCostToCost;

                // Rate after discount:
                const multiplier = isCostToCostProduct
                  ? 1
                  : effectiveDiscountMultiplier;
                const hasDiscount = multiplier < 1;

                const qty = item.Qty || 1;
                const { basePrice, productPrices } = getCartItemEffectivePricing(
                  item,
                  isInternational,
                );
                const unitPrice = getProductUnitPrice(basePrice, productPrices, qty);
                const originalPrice = unitPrice;
                const discountedPrice = originalPrice * multiplier;

                const totalOriginalPrice = originalPrice * qty;
                const totalDiscountedPrice = discountedPrice * qty;

                const priceSlabs = getPriceSlabs(basePrice, productPrices);
                const activeSlab = priceSlabs.find(
                  (slab) =>
                    qty >= slab.minQty &&
                    (slab.maxQty === null || qty <= slab.maxQty),
                ) || priceSlabs[0];

                const isExpanded = !!expandedTiers[item.ProductID];

                return (
                  <div
                    key={item.ProductID}
                    className="p-4 bg-white text-[#1A0A0A] border border-[#ECE5D8] rounded-2xl shadow-xs transition-all duration-300 animate-in fade-in slide-in-from-right-4"
                  >
                    <div className="flex gap-3.5">
                      {/* Product Thumbnail */}
                      <div className="w-18 h-18 bg-[#FAF8F5] border border-[#ECE5D8] rounded-xl overflow-hidden shrink-0 relative flex items-center justify-center p-1">
                        {item.MainImage ? (
                          <img
                            src={item.MainImage}
                            alt={item.ProductName}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#8A5A5A] uppercase text-[9px] font-bold">
                            No Image
                          </div>
                        )}
                      </div>

                      {/* Info & Pricing */}
                      <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div className="flex justify-between items-start gap-2">
                          <h4 className="text-[12px] font-bold text-[#1A0A0A] uppercase tracking-wider truncate">
                            {item.ProductName}
                          </h4>
                          <button
                            onClick={() => handleRemove(item.ProductID)}
                            className="text-[#8A5A5A] hover:text-red-600 transition-colors shrink-0 p-0.5 cursor-pointer"
                            title="Remove Item"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>

                        {/* Controls & Price Row */}
                        <div className="flex items-center justify-between mt-2.5">
                          {/* Qty Controls */}
                          <div className="flex items-center border border-[#E2DACD] bg-white rounded-lg overflow-hidden shadow-2xs">
                            <button
                              onClick={() => handleUpdateQty(item, -1)}
                              disabled={item.Qty <= (item.MinimumQty || 1)}
                              className="w-7 h-7 flex items-center justify-center text-[#1A0A0A] hover:bg-[#F7F4EF] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Minus size={12} />
                            </button>
                            <div className="w-8 text-center text-[#1A0A0A] text-[12px] font-bold border-x border-[#E2DACD] h-7 flex items-center justify-center bg-[#FBF9F5]">
                              {item.Qty}
                            </div>
                            <button
                              onClick={() => handleUpdateQty(item, 1)}
                              disabled={
                                item.AvailableQty !== undefined &&
                                item.Qty >= item.AvailableQty
                              }
                              className="w-7 h-7 flex items-center justify-center text-[#1A0A0A] hover:bg-[#F7F4EF] transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Plus size={12} />
                            </button>
                          </div>

                          {/* Price */}
                          <div className="text-right">
                            {hasDiscount ? (
                              <>
                                <p className="text-[10px] text-[#8A5A5A] line-through leading-none mb-0.5">
                                  ₹{totalOriginalPrice.toLocaleString()}
                                </p>
                                <p className="text-[17px] font-bold text-[#5C2020] font-cormorant leading-none">
                                  ₹{totalDiscountedPrice.toLocaleString()}
                                </p>
                              </>
                            ) : (
                              <p className="text-[17px] font-bold text-[#5C2020] font-cormorant leading-none">
                                ₹{totalOriginalPrice.toLocaleString()}
                              </p>
                            )}
                            <p className="text-[10px] text-[#8A5A5A] mt-0.5 font-medium">
                              ₹{originalPrice.toFixed(2)}/pc
                            </p>
                          </div>
                        </div>
                        {item.AvailableQty !== undefined && (
                          <div className="mt-1 text-[10px]">
                            {item.AvailableQty <= 0 ? (
                              <p className="text-red-600 font-bold uppercase tracking-wider">
                                Out of stock
                              </p>
                            ) : item.Qty > item.AvailableQty ? (
                              <p className="text-red-600 font-bold animate-pulse">
                                Only {item.AvailableQty} available in stock!
                              </p>
                            ) : item.Qty === item.AvailableQty ? (
                              <p className="text-amber-700 font-medium">
                                Max available stock reached ({item.AvailableQty})
                              </p>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Active Tier Badge & Expandable Breakdown */}
                    {priceSlabs.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#ECE5D8] space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 bg-[#FAF7F2] border border-[#EAE3D5] px-2.5 py-1 rounded-lg text-[11px] text-[#332215] font-medium">
                            <Tag size={12} className="text-[#8B5A2B] shrink-0" />
                            <span>
                              Active Tier:{" "}
                              <strong className="font-bold text-[#1A0A0A]">
                                {activeSlab?.rangeLabel} pcs
                              </strong>{" "}
                              @ ₹{activeSlab?.price.toFixed(2)}/pc
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => toggleExpandTier(item.ProductID)}
                            className="bg-white hover:bg-[#FAF7F2] border border-[#DFD6C6] text-[#4A3525] px-2.5 py-1 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition-all cursor-pointer shadow-2xs"
                          >
                            <span>{isExpanded ? "Hide" : "Tiers"}</span>
                            {isExpanded ? (
                              <ChevronUp size={12} />
                            ) : (
                              <ChevronDown size={12} />
                            )}
                          </button>
                        </div>

                        {/* Expandable Breakdown Table */}
                        {isExpanded && (
                          <div className="bg-[#FAF7F2] border border-[#EAE3D5] rounded-xl p-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                            <div className="text-[10px] font-bold uppercase text-[#8B5A2B] tracking-wider mb-1">
                              WHOLESALE QUANTITY PRICE TIERS
                            </div>
                            <div className="space-y-1.5">
                              {priceSlabs.map((slab, idx) => {
                                const isActive =
                                  item.Qty >= slab.minQty &&
                                  (slab.maxQty === null || item.Qty <= slab.maxQty);
                                return (
                                  <div
                                    key={idx}
                                    onClick={() => handleSetQty(item, slab.minQty)}
                                    className={`flex items-center justify-between px-3.5 py-2 rounded-lg text-xs cursor-pointer transition-all ${
                                      isActive
                                        ? "bg-[#FFF8EC] border-2 border-[#D4AF37] text-[#4A3200] font-bold shadow-xs"
                                        : "bg-white text-[#1A0A0A] hover:bg-[#FDFBF7] border border-[#ECE5D8] font-medium"
                                    }`}
                                  >
                                    <span>Qty: {slab.rangeLabel} pcs</span>
                                    <span
                                      className={
                                        isActive
                                          ? "text-[#4A3200] font-bold"
                                          : "text-[#5C2020] font-semibold"
                                      }
                                    >
                                      ₹{slab.price.toFixed(2)} / pc
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {localItems.length > 0 && (
          <div className="mt-auto pt-5 space-y-4 border-t border-border-1">
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-text-3 uppercase tracking-widest">
                <span>Total Items</span>
                <span className="text-text-1 font-bold">{localItems.length}</span>
              </div>
              <div className="flex justify-between items-center bg-gold-core/5 p-3.5 rounded-md border border-gold-dim/20">
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-text-1 uppercase tracking-wider">
                    Subtotal
                  </span>
                  {showLoginDiscount && (
                    <span className="text-[9px] text-green-500 uppercase tracking-widest mt-0.5">
                      Includes 5%{" "}
                      {isCustomerAuthenticated ? "First Order" : "Welcome"}{" "}
                      Discount
                    </span>
                  )}
                  {wholesaleDiscountAmount > 0 && (
                    <span className="text-[9px] text-green-500 uppercase tracking-widest mt-0.5">
                      Includes {wholesaleDiscountPercent}% Wholesale Discount
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end">
                  {totalDiscountAmount > 0 && (
                    <span className="text-[10px] text-text-3 line-through">
                      ₹
                      {subtotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  )}
                  <span className="text-xl font-bold gold-text-gradient font-cormorant">
                    ₹
                    {finalPayable.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2.5">
              <Link
                href="/cart"
                onClick={handleClose}
                className="w-full btn-gold !h-12 flex items-center justify-center gap-3 group"
              >
                <span className="uppercase text-xs font-bold tracking-[0.2em]">
                  Proceed to Cart
                </span>
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
              <button
                onClick={handleClose}
                className="w-full text-[10px] text-text-3 uppercase tracking-[0.2em] font-bold hover:text-text-1 transition-colors py-1.5"
              >
                Continue Shopping
              </button>
            </div>

            <p className="text-[9px] text-text-3 text-center italic opacity-60">
              Tax and shipping calculated at checkout
            </p>
          </div>
        )}
      </div>
    </Drawer>
  );
};



